/**
 * Unified ops risk aggregation contracts (Epic #853 / #857).
 *
 * One severity vocabulary (`critical | warning | info` — the business
 * vocabulary; the P0/P1/P2 scale dies with /ops/v2) and one dimension
 * label set (`supply | finance | traffic`) across every ops surface
 * that renders "how many risks are open".
 *
 * The aggregation itself lives in ops-api
 * (`apps/ops-api/src/util/risk-aggregation.ts`); gateway/platform
 * internal APIs are unchanged. Consumers:
 *
 *   - `GET /v1/ops/risks` — slim shell RiskChip feed (this shape).
 *   - `GET /v1/ops/business/risk` — business dashboard risk queue,
 *     derived from the same module (mapped onto the legacy
 *     `BusinessRiskItem` wire shape for back-compat).
 *
 * No truncation: the risks array carries every open item. The only
 * upper bound is the platform-api list cap (`MAX_LIMIT = 200` per
 * finance class) on the input slices, which is documented on the
 * aggregation module.
 *
 * Window semantics differ per dimension and are carried explicitly in
 * `windows` so UI labels can surface them:
 *
 *   - supply  — trailing-24h dashboard snapshot (gateway + platform
 *               `/internal/health/dashboard-snapshot`).
 *   - finance — unresolved backlog, no time window (rows stay open
 *               until an operator resolves them).
 *   - traffic — the gateway traffic overview's own current window.
 */

import type { OpsTrafficWindowPreset } from './internal-ops-traffic.ts';

export type OpsRiskDimension = 'supply' | 'finance' | 'traffic';
export type OpsRiskSeverity = 'critical' | 'warning' | 'info';

/**
 * Typed risk classes (epic #997 / #1005). One value per fact family —
 * the workbench groups same-type items into a single expandable row and
 * the ack/resolve state machine keys fingerprints as
 * `<type>:<primary-entity-id>`.
 */
export type OpsRiskType =
  | 'finance.billing_anomaly'
  | 'finance.reconciliation_drift'
  | 'finance.unattributed_deductions'
  | 'finance.inconsistent_accounts'
  | 'finance.allocation_backlog'
  /** Allocation queue rows whose usage_logs twin is gone; their cost attribution is still owed. */
  | 'finance.allocation_orphaned_rows'
  /**
   * The `ledger_recompute_lock` kill switch is held (#1574). Six money
   * handlers report `ok: true` while doing nothing for as long as it is — the
   * failure mode is silence, so the risk feed is where the silence gets a
   * voice.
   */
  | 'finance.recompute_lock_held'
  /**
   * A funding batch referenced by `revenue_ledger` no longer exists (#1574).
   * No API deletes batches, so this is evidence of a write that bypassed the
   * API entirely.
   */
  | 'finance.orphaned_funding_batch'
  | 'supply.unrouted_sku'
  | 'supply.incompatible_pin'
  | 'supply.serviceability_projection_stale'
  | 'supply.global_catalog_empty'
  | 'supply.org_catalog_empty'
  | 'supply.single_point_sku'
  | 'supply.low_funding_upstream'
  | 'supply.stranded_funding_capacity'
  | 'supply.unhealthy_upstream'
  | 'traffic.threshold';

/** Operator-owned lifecycle for a risk fingerprint (#1005). */
export type OpsRiskStateStatus = 'open' | 'acked' | 'resolved';

/**
 * Non-default state attached to a risk item. Items with no recorded
 * state (or an explicit `open` row) omit the field — `undefined`
 * means "open".
 */
export interface OpsRiskItemState {
  readonly status: OpsRiskStateStatus;
  readonly actor: string;
  readonly note: string;
  readonly updatedAt: string;
}

export interface OpsRiskItem {
  /** Deterministic id, stable across recomputations of the same facts. */
  readonly id: string;
  readonly dimension: OpsRiskDimension;
  readonly severity: OpsRiskSeverity;
  readonly title: string;
  readonly detail: string;
  /** Label for the remediation link (e.g. "Open billing anomalies"). */
  readonly targetLabel: string;
  /** SPA path of the page where the operator can act on this risk. */
  readonly href: string;
  /** Source-event timestamp when the fact carries one, else null. */
  readonly occurredAt: string | null;
  /**
   * Typed risk class (#1005). Optional on the wire for back-compat
   * with pre-workbench payloads; the ops-api aggregation always emits
   * it.
   */
  readonly type?: OpsRiskType;
  /**
   * Stable ack/resolve key: `<type>:<primary-entity-id>` — never an
   * array index or timestamp, so state survives recomputes (#1005).
   * Optional on the wire for back-compat; always emitted by ops-api.
   */
  readonly fingerprint?: string;
  /** Operator state for `fingerprint`; absent = open. */
  readonly state?: OpsRiskItemState;
  /**
   * Owning tenant account for per-row finance facts (epic #1035 E6-D6,
   * #1070): `finance.billing_anomaly` items carry the source row's
   * `account_id` so owner-scoped surfaces
   * (the customer-manager workbench queue) can route the fact to the
   * owning manager. Aggregate-count facts (reconciliation drift,
   * unattributed deductions) stay global and omit it. ADDITIVE on the
   * wire — same back-compat pattern as `type` / `fingerprint` (#1005).
   */
  readonly accountId?: string;
  /**
   * Owning org, when the emitting surface has already joined
   * account → org (#1070). The core aggregation does not populate it
   * (the risk input slices carry accounts, not orgs); reserved for
   * consumers that decorate items with an ownership join.
   */
  readonly orgId?: string;
}

/**
 * Same-type grouping view (#1005). ADDITIVE alongside the flat
 * `risks` list — consumers of the flat list (shell RiskChip, the
 * cockpit "fires" section from #1004) are unaffected; the workbench
 * renders one expandable row per group. `items` reference the same
 * item shapes that appear in the flat list, in the same order.
 */
export interface OpsRiskGroup {
  readonly groupKey: OpsRiskType;
  readonly dimension: OpsRiskDimension;
  /** Highest severity among `items` (critical > warning > info). */
  readonly severity: OpsRiskSeverity;
  /** Shared human title of the fact family (same for all items). */
  readonly title: string;
  readonly count: number;
  readonly items: readonly OpsRiskItem[];
}

/** Supply risks derive from a trailing-24h health snapshot. */
export type OpsRiskSupplyWindow = '24h_snapshot';
/** Finance risks are the unresolved backlog — not time-windowed. */
export type OpsRiskFinanceWindow = 'unresolved_backlog';

/** Traffic risks reflect the gateway traffic overview's own window. */
export interface OpsRiskTrafficWindow {
  readonly preset: OpsTrafficWindowPreset;
  readonly from: string;
  readonly to: string;
}

export interface OpsRisksWindows {
  readonly supply: OpsRiskSupplyWindow;
  readonly finance: OpsRiskFinanceWindow;
  readonly traffic: OpsRiskTrafficWindow;
}

/**
 * Counts derived from `risks`.
 *
 * Every count here is OPEN-ONLY (#1736): an item an operator has acked or
 * resolved is excluded from `open` and from the severity/dimension counts
 * alike. That is what makes the ack machine mean something — the shell
 * RiskChip renders red on `open > 0`, so a permanently non-empty steady state
 * (the #1407 `supply.org_catalog_empty` entry for an intentionally exclusive
 * org) would otherwise leave the chip red forever with no way to clear it.
 *
 * The invariants hold over the open subset:
 * `critical + warning + info === open === supply + finance + traffic`.
 *
 * `total` carries the unfiltered item count (`=== risks.length`) so the
 * acked/resolved population is never lost: `total - open` is how many items
 * an operator has already dispositioned.
 */
export interface OpsRisksSummary {
  /** Items with no operator state, or an explicit `open` one. */
  readonly open: number;
  /** Every aggregated item regardless of operator state (`risks.length`). */
  readonly total: number;
  readonly critical: number;
  readonly warning: number;
  readonly info: number;
  readonly supply: number;
  readonly finance: number;
  readonly traffic: number;
}

export interface OpsRisksResponse {
  readonly generatedAt: string;
  readonly windows: OpsRisksWindows;
  readonly summary: OpsRisksSummary;
  readonly risks: readonly OpsRiskItem[];
  /**
   * Typed grouping view over `risks` (#1005). Additive: the flat list
   * above stays the complete authoritative payload; `groups` partitions
   * the same items by `type` in first-seen order.
   */
  readonly groups?: readonly OpsRiskGroup[];
  /**
   * Where the balance-source reconciliation behind the finance fact items
   * (`finance.missing_positive_lots`, `finance.unallocated_debits`, …) came
   * from, and when it was computed (#2685). Every other slice is read live.
   */
  readonly balanceSourceCheck?: OpsRisksBalanceSourceCheck;
}

/**
 * `GET /v1/ops/risks?balanceSources=` — which reconciliation the finance
 * fact items are built from.
 *
 * - `last_computed` (default; the shell RiskChip): the last result any read
 *   computed, whatever its age. This read never runs the full-history scan.
 * - `live` (the risk workbench): computed for this read, or platform's own
 *   copy up to five minutes old.
 */
export type OpsRisksBalanceSourcesMode = 'last_computed' | 'live';

export interface OpsRisksBalanceSourceCheck {
  readonly source: OpsRisksBalanceSourcesMode;
  /**
   * The reconciliation's own `generated_at`. `null` only under
   * `last_computed` when nothing has been computed yet: the finance fact
   * items are then absent because nobody checked — not because the check
   * came back clean — and a consumer must say so rather than show zero.
   */
  readonly computedAt: string | null;
  /**
   * `computedAt` is older than {@link BALANCE_SOURCE_CHECK_STALE_AFTER_MS}
   * at the moment ops-api answered (false when `computedAt` is null — that
   * state has its own rendering). The shell chip renders this flag and holds
   * no threshold of its own, so the console and ops-api cannot disagree on
   * the number. Optional on the wire: an ops-api older than the flag omits it.
   */
  readonly stale?: boolean;
}

/**
 * Cadence of control-loops `balance_source_health_refresh` (#2686 review M2):
 * it calls platform's computing `/balance-sources/health` read, which also
 * refreshes the fleet-shared Tair copy the shell chip reads. Without it the
 * chip's finance facts are only as fresh as the last time someone opened the
 * risk workbench or the revenue page. The full-history scan is ~45 s on
 * production, so four runs a day, not more.
 */
export const BALANCE_SOURCE_HEALTH_REFRESH_INTERVAL_MINUTES = 360;

/**
 * When the balance-source check behind the finance fact items counts as
 * stale: one refresh interval plus one hour of grace (a late tick, a scan that
 * failed once and is picked up by the next explicit read). Past this the chip
 * stops showing green — see `OpsRisksBalanceSourceCheck.stale`.
 */
export const BALANCE_SOURCE_CHECK_STALE_AFTER_MS =
  (BALANCE_SOURCE_HEALTH_REFRESH_INTERVAL_MINUTES + 60) * 60 * 1000;

/** Single definition of the staleness rule; ops-api stamps its result on the wire. */
export function isBalanceSourceCheckStale(computedAt: string | null, nowMs: number): boolean {
  if (computedAt === null) return false;
  const computedMs = Date.parse(computedAt);
  // An unparseable timestamp proves nothing about freshness: stale.
  if (!Number.isFinite(computedMs)) return true;
  return nowMs - computedMs > BALANCE_SOURCE_CHECK_STALE_AFTER_MS;
}

// ─── Risk state machine (platform-owned `ops_risk_states`, #1005) ──────

/**
 * One `ops_risk_states` row. Platform-api owns the schema (migration
 * 0070); ops-api composes rows onto aggregated items by fingerprint.
 * The row keeps the LAST transition (status/actor/note/updated_at) —
 * history lives in `ops_audit` (`risk.ack` / `risk.resolve` /
 * `risk.reopen` actions).
 */
export interface OpsRiskStateRecord {
  readonly fingerprint: string;
  readonly status: OpsRiskStateStatus;
  readonly actor: string;
  readonly note: string;
  readonly created_at: string;
  readonly updated_at: string;
}

/** `GET /internal/ops/risk-states` (platform-api). */
export interface ListOpsRiskStatesResponse {
  readonly states: readonly OpsRiskStateRecord[];
}

/**
 * `POST /internal/ops/risk-states/set` (platform-api) and
 * `POST /v1/ops/risks/state` (ops-api). The ops-api proxy attributes
 * the actor from the session via the `X-Ops-User-Id` header — a
 * client-supplied actor is never trusted.
 */
export interface SetOpsRiskStateRequest {
  readonly fingerprint: string;
  readonly status: OpsRiskStateStatus;
  readonly note?: string;
}

export interface SetOpsRiskStateResponse {
  readonly state: OpsRiskStateRecord;
}
